import { useCallback, useEffect, useRef } from 'react';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import type { RelayError } from '@confluence/relay-errors/entry-points/RelayError.ts';

import { classifyGraphError, reportHandledGraphError } from '../utils/graphErrorReporting.ts';

import { getIncomingLinksFailedEvent } from './incomingLinksAnalytics.ts';
import {
	useQueryLoadingDuration,
	type QueryLoadingDurationAttributes,
} from './useQueryLoadingDuration.ts';

export type IncomingLinksQueryAnalyticsOptions = {
	enabled: boolean;
	identity: string;
	loading: boolean;
	data: unknown;
	error: RelayError | undefined;
};

export const useIncomingLinksQueryAnalytics = ({
	enabled,
	identity,
	loading,
	data,
	error,
}: IncomingLinksQueryAnalyticsOptions): (() => QueryLoadingDurationAttributes) => {
	const { createEvent } = useConfluenceAnalyticsEvents();
	const completed = useRef<{ identity: string; duration: QueryLoadingDurationAttributes }>();
	const getDuration = useQueryLoadingDuration({ enabled, identity, loading, result: data, error });

	useEffect(() => {
		if (!enabled || completed.current?.identity !== identity) {
			completed.current = undefined;
		}
		if (!enabled || loading || completed.current || (data === undefined && !error)) {
			return;
		}
		// Later fetchMore errors belong to incomingLinksPage, even if query loading stays false.
		const duration = getDuration(data, error);
		completed.current = { identity, duration };
		if (!error) {
			return;
		}
		try {
			reportHandledGraphError(error);
		} catch {
			// A monitoring failure must not discard usable partial rows.
		}
		try {
			createEvent(
				getIncomingLinksFailedEvent({
					errorType: classifyGraphError(error),
					hasQueryData: data != null,
					...duration,
				}),
			).fire();
		} catch {
			// Partial rows must remain usable even when analytics reporting fails.
		}
	}, [createEvent, data, enabled, identity, loading, error, getDuration]);

	// Keep the hydration effect stable while the initial query settles.
	return useCallback(() => completed.current?.duration ?? {}, []);
};
